//AEATEAM-724: the content of this file should be copied to FootnotesHelper.ts when globalelements solution is to move to production
import { Footnote, FootnotesState } from './FootnotesReducerTypes';
import { connectionName, FootnotesConnection, FootnotesDispatchers } from './FootnotesReducer';
import { initializeStore, SeamlessCoreStore } from '@seamless/store';
import Logger from '@owc-utils/common/Logger';
import { getFootnotesIfAvailable } from './FootnotesUtils';
import { BaseDataJson } from '@owc-types/BaseDataJSON';
/**
 * add a connection to the seamless store
 *
 * @function addFootnotesConnection
 * @returns seamlessStore
 */
const addFootnotesConnection = async (): Promise<void> => {
  const store = initializeStore();
  return store.addConnection(new FootnotesConnection()).catch(() => Logger.debug('connection already added'));
};

/**
 * the connection from the FootnotesConnection
 *
 * @function getFootnotesConnection
 * @return Public dispatchers
 */
const getFootnotesConnection = async (): Promise<FootnotesDispatchers> => {
  const store = initializeStore();
  await addFootnotesConnection();
  return store.getConnectionDispatchers(connectionName);
};

let store: SeamlessCoreStore;

export let isFootnoteLoaded: boolean;
const GLOBALELEM_FOOTNOTE_REFERENCE_COMPONENT = 'globalelem-footnote-reference-component';
const GLOBALELEM_FOOTNOTE_COMPONENT = 'globalelem-footnote-component';
const GLOBALELEM_FOOTNOTE_COMPONENTS = [
  {
    tag: GLOBALELEM_FOOTNOTE_COMPONENT,
    package: 'footnote-component',
  },
  {
    tag: GLOBALELEM_FOOTNOTE_REFERENCE_COMPONENT,
    package: 'footnote-reference-component',
  },
];

//This is the list of components whose footnotes should be listed in their immediate parent
const NESTED_FOOTNOTES = [
  'OWC-MEDIA-SLIDER-ITEM',
  'OWC-MEDIA-GALLERY-ITEM',
  'OWC-SIMPLE-TEASER-ITEM',
  'OWC-FEATURE-BOX-ITEM',
  'OWC-TABLE-CELL',
];

/**
 * Adds a list of footnotes to the store
 * ```js
 * import { addFootnotesToStore } from '@owc-utils/Footnotes/FootnotesHelper';
 * addFootnotesToStore(this.footnotesData.footnotes);
 * ```
 * @exports
 * @function addFootnotesToStore
 * @param footnotes
 * @returns Promise
 */
export const addFootnotesToStore = async (footnotes: Footnote[]) => {
  const footnotesConnection = await getFootnotesConnection();
  footnotesConnection.addFootnotes(footnotes);
};

export const getLocalFootnotes = (data?: BaseDataJson): Footnote[] | undefined => {
  return getFootnotesIfAvailable(data)?.footnotes?.filter(footnote => footnote.local === true);
};

const loadFootnoteComponent = async (component: Record<string, string>): Promise<void> => {
  if (!window.customElements.get(component.tag)) {
    const { defineCustomElements } = await import(`@dh-io-globalelem/${component.package}/dist/loader`);
    await defineCustomElements(window);
  }
};

export const loadFootnoteComponents = (): void => {
  if (!isFootnoteLoaded) {
    isFootnoteLoaded = true;
    GLOBALELEM_FOOTNOTE_COMPONENTS.forEach(async component => await loadFootnoteComponent(component));
  }
};

const referencedFootnoteIds: Record<string, string>[] = [];

const setUniqueReference = (footnoteId: string): string => {
  const uniqueId: number = referencedFootnoteIds.filter((el: Record<string, string>) => el.id === footnoteId)
    .length;
  return `${uniqueId + 1}`;
};

//Sets the footnote reference of the globalElement footnote reference component
export const setReference = (component: any, footnoteId: string): string => {
  const { tagName, componentId } = component.$el.getRootNode().host;
  const moduleId = `${tagName
    .replace('owc-', '')
    .split('-')
    .join('')}`;
  const reference = `owc_${moduleId}${setUniqueReference(footnoteId)}_${componentId}`;
  return reference.toLowerCase();
};

//Sets the footnote moduleId of the globalElement footnote reference component
export const setModuleId = (component: any): string => {
  if (component.$el) {
    const componentElement = component.$el.getRootNode().host;
    const isNested = NESTED_FOOTNOTES.includes(componentElement.tagName);

    const moduleId = `owc-${
      isNested
        ? component.parentId || componentElement.getRootNode().host?.componentId
        : componentElement.componentId
    }`;

    return moduleId;
  }
  return '';
};

/**
 * Mounts the footnotes elements inside the component (with <sup> element)
 * ```js
 * import { mountFootnotes } from '@owc-utils/Footnotes/FootnotesHelper';
 * mountFootnotes(domElement);
 * ```
 * eg.
 * <p>Your text goes here with <span data-footnote-id="abc123xyz">footnote</span></p>
 * turns into
 * <p>Your text goes here with <span data-footnote-id="abc123xyz">footnote<sup>1</sup></span></p>
 * @function mountFootnotes
 * @exports
 * @param componentElement
 */

export const mountFootnotes = async (component: any) => {
  //TODO: ??=
  if (!store) {
    store = initializeStore();
  }

  const footnoteElements: HTMLElement[] = Array.from(component.$el.querySelectorAll('[data-footnote-id]'));

  // Don't subscribe if there's no footnote elements
  if (footnoteElements.length === 0) return;

  store.subscribe(connectionName, ({ footnotes: allFootnotes }: FootnotesState) => {
    footnoteElements.forEach(footnote => {
      const currentFootnoteId = footnote.dataset.footnoteId as string;
      const currentFootnote = allFootnotes[currentFootnoteId];

      if (currentFootnote && !footnote.querySelector(GLOBALELEM_FOOTNOTE_REFERENCE_COMPONENT)) {
        const { text, local } = currentFootnote;

        footnote.insertAdjacentHTML(
          'beforeend',
          `<${GLOBALELEM_FOOTNOTE_REFERENCE_COMPONENT} 
            content="${text.replace(/"/g, '&quot;')}"
            reference="${setReference(component, currentFootnoteId)}"
            ${local ? `module-id="${setModuleId(component)}"` : ''}
          ></${GLOBALELEM_FOOTNOTE_REFERENCE_COMPONENT}>`,
        );
        referencedFootnoteIds.push({ id: currentFootnoteId });
      }
    });
  });
};
